import { Divider } from '@design-system';
import { useTranslation } from 'next-i18next';
import { FC, useEffect, useMemo } from 'react';
import SbEditable from 'storyblok-react';

import { GLOSSARY_TERM_PAGE } from '../../constants';
import { pathFor } from '../../helpers/links';
import { AugmentedArtistCard } from '../../types/Artists';
import { GlossaryIndex, GlossaryTerm } from '../../types/Glossary';
import { TrackPageView } from '../../utils/analytics';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { AnalyticsObjectProvider } from '../../utils/analytics/object';
import { GlossaryLink } from '../../utils/glossary';
import { InsightItem } from '../../utils/journals/insights';
import { PreviewItem } from '../../utils/storyblok/stories';
import { BreadcrumbsList } from '../Layout/Breadcrumbs/BreadcrumbsList';
import PageHeading from '../Layout/PageHeading';
import PageSection from '../Layout/PageSection';
import { LinkCardGroup } from '../LinkCards';
import { SupportFAQ } from '../Support/SupportFAQ/SupportFAQ';
import { GlossaryArticle } from './GlossaryArticle';
import { GlossaryArtists } from './GlossaryArtists';
import { GlossaryCarousel } from './GlossaryCarousel';
import { GlossaryDetails } from './GlossaryDetails';
import { GlossaryInsights } from './GlossaryInsights';
import { GlossaryResults } from './GlossaryResults';

interface Props {
  artists: AugmentedArtistCard[];
  glossary: GlossaryLink[];
  index: GlossaryIndex | null;
  insights: InsightItem[];
  page: GlossaryTerm;
  relatedTerms: GlossaryLink[];
  results: PreviewItem[];
}

export const GlossaryTermPage: FC<Props> = ({
  artists,
  glossary,
  index,
  insights,
  page,
  relatedTerms,
  results,
}) => {
  const { t } = useTranslation('glossary');

  const properties = useMemo(
    () => ({
      results_count: results.length,
      term: page.content.term,
    }),
    [page.content.term, results.length],
  );

  const [analyticsEmitter] = useAnalyticsEmitter(properties);

  useEffect(() => {
    TrackPageView({
      page: GLOSSARY_TERM_PAGE,
      properties,
    });
  }, [properties]);

  return (
    // @ts-expect-error require storyblok-react upgrade
    <SbEditable key={page.content._uid} content={page.content}>
      <BreadcrumbsList
        items={[
          { href: pathFor({ page: 'GLOSSARY_INDEX' }), name: t('glossary') },
          { href: '', name: page.content.term },
        ]}
        onClick={() => {
          analyticsEmitter('glossary', 'breadcrumb click');
        }}
      />

      <main className="my-9 space-y-9 md:my-12 md:space-y-12">
        <div className="space-y-6">
          <PageSection>
            <PageHeading align="center" heading={page.content.term} />
          </PageSection>

          {page.content.description && (
            <PageSection>
              <GlossaryDetails
                description={page.content.description}
                onClick={(term) => {
                  analyticsEmitter('glossary', 'related term click', { term });
                }}
                relatedTerms={relatedTerms}
                title={t('relatedWords')}
              />
            </PageSection>
          )}
        </div>

        {page.content.article && (
          <PageSection>
            <AnalyticsObjectProvider name="glossary" properties={properties}>
              {() => <GlossaryArticle content={page.content.article || []} />}
            </AnalyticsObjectProvider>
          </PageSection>
        )}

        {page.content.term && results.length > 0 && (
          <PageSection>
            <GlossaryResults items={results} term={page.content.term} />
          </PageSection>
        )}

        {page.content.term && artists.length > 0 && (
          <PageSection fullWidth>
            <GlossaryArtists
              artists={artists}
              onClick={(artist) => {
                analyticsEmitter('glossary', 'artist click', {
                  name: artist.name,
                  slug: artist.slug,
                });
              }}
              onSlide={(position) => {
                analyticsEmitter('glossary', 'artists slider click', {
                  position,
                });
              }}
              title={t('artistsTitle', { term: page.content.term })}
            />
          </PageSection>
        )}

        {page.content.term && insights.length > 0 && (
          <PageSection fullWidth>
            <GlossaryInsights
              insights={insights}
              onClick={(insight) => {
                analyticsEmitter('glossary', 'insight click', {
                  title: insight.title,
                  slug: insight.full_slug,
                });
              }}
              onSlide={(position) => {
                analyticsEmitter('glossary', 'insights slider click', {
                  position,
                });
              }}
              title={t('insightsTitle', { term: page.content.term })}
            />
          </PageSection>
        )}

        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>

        <PageSection fullWidth>
          <GlossaryCarousel
            handleClick={(term) => {
              analyticsEmitter('glossary', 'list term click', { term });
            }}
            handleSlide={(position) => {
              analyticsEmitter('glossary', 'list slide', { position });
            }}
            items={glossary}
            title={t('carouselTitle')}
          />
        </PageSection>

        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>

        {index?.content.linkCardGroup?.[0] ? (
          <PageSection>
            <LinkCardGroup
              key={index?.content.linkCardGroup[0]._uid}
              analyticsPlacement={
                index?.content.linkCardGroup[0].analyticsPlacement
              }
              cards={index?.content.linkCardGroup[0].cards}
            />
          </PageSection>
        ) : null}

        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>

        <PageSection>
          <SupportFAQ
            analytics={{
              page: GLOSSARY_TERM_PAGE,
              context: 'FAQ',
            }}
            faq={index?.content?.faqContent?.story?.content}
            image={index?.content.faqImage}
            title={t('faq.title')}
          />
        </PageSection>
      </main>
    </SbEditable>
  );
};
